import { ChevronRight, RectangleHorizontal } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { getLessonTypeVisual } from "@/lib/academy/lesson-visuals"

type LessonViewerHeaderProps = {
  courseTitle: string
  sectionTitle?: string | null
  lessonTitle: string
  lessonType?: string | null
  orderIndex: number
  durationMinutes?: number | null
  /** Modo cine (solo se ofrece en escritorio). */
  theater?: boolean
  onToggleTheater?: () => void
}

/** Cabecera compacta: el video debe quedar visible sin hacer scroll. */
export default function LessonViewerHeader({
  courseTitle,
  sectionTitle,
  lessonTitle,
  lessonType,
  orderIndex,
  durationMinutes,
  theater = false,
  onToggleTheater,
}: LessonViewerHeaderProps) {
  const { icon: LessonIcon } = getLessonTypeVisual({ lesson_type: lessonType })

  return (
    <header className="mb-3 flex items-end justify-between gap-4">
      <div className="min-w-0">
        <nav aria-label="Ubicación en el curso" className="mb-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate max-w-[12rem]">{courseTitle}</span>
          {sectionTitle && (
            <>
              <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate max-w-[12rem]">{sectionTitle}</span>
            </>
          )}
        </nav>
        <h1 className="truncate text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          {lessonTitle}
        </h1>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Badge className="bg-primary/20 text-primary border-primary/30 hover:bg-primary/30">
          Lección {orderIndex}
        </Badge>
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <LessonIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {durationMinutes ? `${durationMinutes} min` : null}
        </div>
        {onToggleTheater && (
          <button
            type="button"
            onClick={onToggleTheater}
            aria-pressed={theater}
            title={theater ? "Salir del modo cine" : "Modo cine"}
            className="motion-press hidden min-h-9 items-center gap-1.5 rounded-lg border border-border bg-foreground/5 px-3 text-xs font-semibold text-foreground outline-none transition-colors hover:bg-foreground/10 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-pressed:border-primary/50 aria-pressed:text-primary lg:inline-flex"
          >
            <RectangleHorizontal className="h-4 w-4" aria-hidden="true" />
            Modo cine
          </button>
        )}
      </div>
    </header>
  )
}
